import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getCalendarEvents, createCalendarEvent, updateCalendarEvent, deleteCalendarEvent, checkTaskConflict } from '../services/calendar';
import { getClients } from '../services/clients';
import { getAllUsers } from '../services/users';
import { getTaskTypes } from '../services/taskTypes';
import { createAuditLog } from '../services/audit';
import { createTaskNotification } from '../services/notifications';
import './Calendar.css';

// Centralized task type color system
const taskTypeColors = {
  general: {
    background: "#dbeafe",
    border: "#3b82f6",
    text: "#1e40af"
  },
  call: {
    background: "#fef3c7",
    border: "#f59e0b",
    text: "#92400e"
  },
  follow_up: {
    background: "#fde68a",
    border: "#fbbf24",
    text: "#78350f"
  },
  estimate_follow_up: {
    background: "#fed7aa",
    border: "#fb923c",
    text: "#9a3412"
  },
  invoice_follow_up: {
    background: "#fecaca",
    border: "#f87171",
    text: "#991b1b"
  },
  site_visit: {
    background: "#d1fae5",
    border: "#34d399",
    text: "#065f46"
  },
  measurement: {
    background: "#e0e7ff",
    border: "#6366f1",
    text: "#3730a3"
  },
  install: {
    background: "#ddd6fe",
    border: "#8b5cf6",
    text: "#4c1d95"
  },
  // Legacy colors for backward compatibility
  overdue: {
    background: "#fee2e2",
    border: "#ef4444",
    text: "#991b1b"
  },
  estimate: {
    background: "#fef3c7",
    border: "#f59e0b",
    text: "#92400e"
  },
  invoice: {
    background: "#dbeafe",
    border: "#2563eb",
    text: "#1e3a8a"
  },
  completed: {
    background: "#d1fae5",
    border: "#10b981",
    text: "#065f46"
  },
  neutral: {
    background: "#e5e7eb",
    border: "#6b7280",
    text: "#374151"
  }
};

export default function Calendar() {
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventFormData, setEventFormData] = useState({
    title: '',
    description: '',
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
    all_day: false,
    client_id: '',
    assigned_to: '',
    task_type: 'general',
    status: 'scheduled',
    require_arrival_time: null,
    require_start_time: null,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [taskListOpen, setTaskListOpen] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completingTaskId, setCompletingTaskId] = useState(null);
  const [completionNote, setCompletionNote] = useState('');
  const [createFollowUp, setCreateFollowUp] = useState(false);
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpAssignee, setFollowUpAssignee] = useState('');
  const [completingTaskTypeConfig, setCompletingTaskTypeConfig] = useState(null);
  const [rescheduleNeeded, setRescheduleNeeded] = useState(false);
  const [measurementData, setMeasurementData] = useState({ description: '', quantity_dimensions: '', specifications: '', comments: '' });
  const [showArrivalStartModal, setShowArrivalStartModal] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [statusChangeTime, setStatusChangeTime] = useState('');

  const toDateTimeLocal = (d) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const loadClients = useCallback(async () => {
    try {
      const data = await getClients();
      setClients(data);
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const data = await getAllUsers();
      setUsers(data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  }, []);

  const loadTaskTypes = useCallback(async () => {
    try {
      const data = await getTaskTypes(false);
      setTaskTypes(data || []);
    } catch (error) {
      console.error('Error loading task types:', error);
      setTaskTypes([]);
    }
  }, []);

  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const startDate = new Date(year, month, 1);
      const endDate = new Date(year, month + 1, 0, 23, 59, 59);
      const data = await getCalendarEvents(startDate, endDate);
      setEvents(data);
    } catch (error) {
      console.error('Error loading events:', error);
      setError('Failed to load events: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [currentDate]);

  useEffect(() => {
    loadClients();
    loadUsers();
    loadTaskTypes();
  }, [loadClients, loadUsers, loadTaskTypes]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const getEventsForDate = (date) => {
    if (!date) return [];
    return events.filter(event => {
      const eventDate = new Date(event.start_date);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  const handleDateClick = (date) => {
    if (!date) return;
    setEditingEvent(null);
    const dateStr = date.toISOString().split('T')[0];
    setEventFormData({
      title: '',
      description: '',
      start_date: dateStr,
      start_time: '09:00',
      end_date: dateStr,
      end_time: '10:00',
      all_day: false,
      client_id: '',
      assigned_to: '',
      task_type: 'general',
      status: 'scheduled',
      require_arrival_time: null,
      require_start_time: null,
    });
    setShowEventModal(true);
  };

  const handleEventClick = async (event) => {
    // Prevent editing completed tasks
    if (event.status === 'completed') {
      alert('This task is completed and cannot be edited.');
      return;
    }
    
    setEditingEvent(event);
    const startDate = new Date(event.start_date);
    const endDate = event.end_date ? new Date(event.end_date) : startDate;
    
    setEventFormData({
      title: event.title,
      description: event.description || '',
      start_date: startDate.toISOString().split('T')[0],
      start_time: event.all_day ? '' : startDate.toTimeString().slice(0, 5),
      end_date: endDate.toISOString().split('T')[0],
      end_time: event.all_day ? '' : (endDate.toTimeString().slice(0, 5)),
      all_day: event.all_day,
      client_id: event.client_id || '',
      assigned_to: event.assigned_to || '',
      task_type: event.task_type || 'general',
      status: event.status || 'scheduled',
      require_arrival_time: event.require_arrival_time ?? null,
      require_start_time: event.require_start_time ?? null,
    });
    setShowEventModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Prevent updating completed tasks
    if (editingEvent && editingEvent.status === 'completed') {
      setError('Completed tasks cannot be updated.');
      return;
    }

    try {
      const startDateTime = eventFormData.all_day
        ? new Date(eventFormData.start_date + 'T00:00:00')
        : new Date(eventFormData.start_date + 'T' + eventFormData.start_time);
      
      const endDateTime = eventFormData.all_day
        ? (eventFormData.end_date ? new Date(eventFormData.end_date + 'T23:59:59') : null)
        : (eventFormData.end_date && eventFormData.end_time ? new Date(eventFormData.end_date + 'T' + eventFormData.end_time) : null);

      // If no end date/time provided, default to 1 hour after start
      const finalEndDateTime = endDateTime || (eventFormData.all_day ? null : new Date(startDateTime.getTime() + 60 * 60 * 1000));

      // Check for task conflicts if a user is assigned
      if (eventFormData.assigned_to) {
        const conflicts = await checkTaskConflict(
          eventFormData.assigned_to,
          startDateTime,
          finalEndDateTime,
          eventFormData.all_day,
          editingEvent ? editingEvent.id : null
        );

        if (conflicts.length > 0) {
          const conflictTitles = conflicts.map(c => c.title).join(', ');
          const conflictUser = users.find(u => u.id === eventFormData.assigned_to);
          const userName = conflictUser?.full_name || conflictUser?.email || 'the selected user';
          setError(`${userName} already has ${conflicts.length} task(s) scheduled at this time: ${conflictTitles}. Please choose a different time or assign to a different user.`);
          return;
        }
      }

      // Validate required fields
      if (!eventFormData.assigned_to) {
        setError('Assignee is required. Please select a user to assign this task to.');
        return;
      }

      const eventData = {
        title: eventFormData.title,
        description: eventFormData.description,
        start_date: startDateTime.toISOString(),
        end_date: finalEndDateTime ? finalEndDateTime.toISOString() : null,
        all_day: eventFormData.all_day,
        client_id: eventFormData.client_id || null,
        assigned_to: eventFormData.assigned_to, // Required - no null fallback
        task_type: eventFormData.task_type || 'general',
        status: eventFormData.status || 'scheduled',
        require_arrival_time: eventFormData.require_arrival_time ?? null,
        require_start_time: eventFormData.require_start_time ?? null,
        created_by: editingEvent ? undefined : user?.id,
      };

      if (editingEvent) {
        await updateCalendarEvent(editingEvent.id, eventData);
        await createAuditLog('update', 'calendar_event', editingEvent.id, {
          title: eventData.title,
          client_id: eventData.client_id,
          status: eventData.status,
        });
        setSuccess('Event updated successfully!');
      } else {
        const newEvent = await createCalendarEvent(eventData);
        await createAuditLog('create', 'calendar_event', newEvent.id, {
          title: eventData.title,
          client_id: eventData.client_id,
        });
        setSuccess('Event created successfully!');
      }

      await loadEvents();
      setTimeout(() => {
        setShowEventModal(false);
        setSuccess('');
      }, 1000);
    } catch (error) {
      setError(error.message || 'Failed to save event');
    }
  };

  const handleDelete = async (eventId) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        await deleteCalendarEvent(eventId);
        await createAuditLog('delete', 'calendar_event', eventId, {});
        await loadEvents();
        setShowEventModal(false);
        setSuccess('Event deleted successfully!');
        setTimeout(() => setSuccess(''), 2000);
      } catch (error) {
        setError('Failed to delete event: ' + error.message);
      }
    }
  };

  const handleStatusChange = async (eventId, newStatus) => {
    // Find the event to check if it's already completed
    const event = events.find(e => e.id === eventId);
    if (event && event.status === 'completed') {
      setError('Completed tasks cannot be updated.');
      return;
    }

    // If completing, check task type for conditional behavior
    if (newStatus === 'completed') {
      const taskTypeCode = event?.task_type || 'general';
      const typeConfig = taskTypes.find(tt => tt.code === taskTypeCode);
      const requiresModal = taskTypes.length > 0
        ? typeConfig?.requires_completion_modal === true
        : ['general', 'call', 'follow_up', 'estimate_follow_up', 'invoice_follow_up', 'measurement', 'install'].includes(taskTypeCode);

      if (requiresModal) {
        setCompletingTaskId(eventId);
        setCompletingTaskTypeConfig(typeConfig || {
          completion_actions: { allow_completion_note: true, allow_create_follow_up: true },
        });
        setCompletionNote('');
        setCreateFollowUp(false);
        setFollowUpDate('');
        setFollowUpAssignee(event?.assigned_to || '');
        setRescheduleNeeded(false);
        setMeasurementData({ description: '', quantity_dimensions: '', specifications: '', comments: '' });
        setShowCompletionModal(true);
        return;
      } else {
        // Task type has no completion modal: complete immediately
        try {
          await updateCalendarEvent(eventId, { status: 'completed' });
          await createAuditLog('update', 'calendar_event', eventId, {
            status: 'completed',
            action: 'task_completed',
          });
          await loadEvents();
          setSuccess('Task completed successfully!');
          setTimeout(() => setSuccess(''), 2000);
        } catch (error) {
          setError('Failed to complete task: ' + error.message);
        }
        return;
      }
    }

    // For 'arrived' or 'started', check if this task or task type requires time entry
    const taskTypeCode = event?.task_type || 'general';
    const typeConfig = taskTypes.find(tt => tt.code === taskTypeCode);
    const typeActions = typeConfig?.completion_actions || {};
    const requireArrival = event.require_arrival_time != null ? event.require_arrival_time : (typeActions.require_arrival_time === true);
    const requireStart = event.require_start_time != null ? event.require_start_time : (typeActions.require_start_time === true);
    if (newStatus === 'arrived' && requireArrival) {
      setPendingStatusChange({ eventId, newStatus: 'arrived' });
      setStatusChangeTime(toDateTimeLocal(new Date()));
      setShowArrivalStartModal(true);
      return;
    }
    if (newStatus === 'started' && requireStart) {
      setPendingStatusChange({ eventId, newStatus: 'started' });
      setStatusChangeTime(toDateTimeLocal(new Date()));
      setShowArrivalStartModal(true);
      return;
    }

    // Proceed with status change (no time required or not arrived/started)
    try {
      const updates = { status: newStatus };
      if (newStatus === 'arrived') updates.arrival_time = null;
      if (newStatus === 'started') updates.started_at = null;
      await updateCalendarEvent(eventId, updates);
      await createAuditLog('update', 'calendar_event', eventId, {
        status: newStatus,
        action: 'status_change',
      });
      await loadEvents();
      if (editingEvent && editingEvent.id === eventId) {
        setEventFormData({ ...eventFormData, status: newStatus });
      }
      setSuccess(`Status updated to ${newStatus}!`);
      setTimeout(() => setSuccess(''), 2000);
    } catch (error) {
      setError('Failed to update status: ' + error.message);
    }
  };

  const handleArrivalStartTimeConfirm = async () => {
    if (!pendingStatusChange) return;
    const { eventId, newStatus } = pendingStatusChange;
    const iso = statusChangeTime ? new Date(statusChangeTime).toISOString() : null;
    try {
      const updates = { status: newStatus };
      if (newStatus === 'arrived') updates.arrival_time = iso;
      if (newStatus === 'started') updates.started_at = iso;
      await updateCalendarEvent(eventId, updates);
      await createAuditLog('update', 'calendar_event', eventId, {
        status: newStatus,
        action: 'status_change',
        ...(newStatus === 'arrived' && iso && { arrival_time: iso }),
        ...(newStatus === 'started' && iso && { started_at: iso }),
      });
      await loadEvents();
      const event = events.find(e => e.id === eventId);
      if (editingEvent && event && editingEvent.id === eventId) {
        setEventFormData(prev => ({ ...prev, status: newStatus }));
      }
      setSuccess(`Status updated to ${newStatus}!`);
      setTimeout(() => setSuccess(''), 2000);
      setShowArrivalStartModal(false);
      setPendingStatusChange(null);
      setStatusChangeTime('');
    } catch (err) {
      setError('Failed to update status: ' + err.message);
    }
  };

  const handleCompleteTask = async () => {
    const actions = completingTaskTypeConfig?.completion_actions || {};
    const requiresMeasurements = actions.requires_measurements === true;
    const allowReschedule = actions.allow_reschedule_needed === true;
    const requireNoteWhenReschedule = actions.require_note_when_reschedule === true;
    const requireCompletionNote = actions.require_completion_note === true;

    if (createFollowUp && (!followUpDate || !followUpAssignee)) {
      setError('Follow-up date and assignee are required when creating a follow-up task.');
      return;
    }

    if (rescheduleNeeded && allowReschedule) {
      if (requireNoteWhenReschedule && !completionNote?.trim()) {
        setError('A note is required when reschedule is needed.');
        return;
      }
    } else if (requiresMeasurements) {
      const { description, quantity_dimensions, specifications, comments } = measurementData;
      if (!description?.trim() && !quantity_dimensions?.trim() && !specifications?.trim() && !comments?.trim()) {
        setError('Please fill in at least one measurement field (description, quantity/dimensions, specifications, or comments).');
        return;
      }
    } else if (requireCompletionNote && !completionNote?.trim()) {
      setError('Completion note is required for this task type.');
      return;
    }

    if (!completingTaskId) return;

    const event = events.find(e => e.id === completingTaskId);
    if (!event) {
      setError('Task not found.');
      setShowCompletionModal(false);
      return;
    }

    let finalNote = completionNote?.trim() || '';
    if (requiresMeasurements && !rescheduleNeeded) {
      const measurementNote = JSON.stringify({
        description: measurementData.description || '',
        quantity_dimensions: measurementData.quantity_dimensions || '',
        specifications: measurementData.specifications || '',
        comments: measurementData.comments || '',
        ...(finalNote && { completion_note: finalNote }),
      });
      finalNote = measurementNote;
    } else if (rescheduleNeeded && finalNote) {
      finalNote = '[Reschedule needed] ' + finalNote;
    }

    try {
      await updateCalendarEvent(completingTaskId, { 
        status: 'completed',
        completion_note: finalNote || null
      });

      const currentUser = users.find(u => u.id === user?.id) || {};
      const clientName = event.clients?.name || 'No Client';

      const notifyUserId = event.created_by || event.assigned_to;
      if (notifyUserId) {
        await createTaskNotification({
          task_id: completingTaskId,
          task_title: event.title,
          completed_by: user?.id,
          completed_by_name: currentUser.full_name || currentUser.email || 'Unknown User',
          completion_note: finalNote || '',
          client_id: event.client_id,
          client_name: clientName,
          assigned_to: notifyUserId,
          is_read: false
        });
      }

      // Create follow-up task if requested
      if (createFollowUp && followUpDate && followUpAssignee) {
        const followUpDateTime = new Date(followUpDate + 'T09:00:00');
        const followUpEndDateTime = new Date(followUpDateTime.getTime() + 60 * 60 * 1000); // 1 hour default

        const followUpData = {
          title: `Follow-up: ${event.title}`,
          description: completionNote ? `Completion notes: ${completionNote}\n\nFollow-up from: ${event.title}` : `Follow-up from: ${event.title}`,
          start_date: followUpDateTime.toISOString(),
          end_date: followUpEndDateTime.toISOString(),
          all_day: false,
          client_id: event.client_id,
          assigned_to: followUpAssignee,
          task_type: 'follow_up',
          status: 'scheduled',
          created_by: user?.id,
        };

        await createCalendarEvent(followUpData);
        await createAuditLog('create', 'calendar_event', null, {
          title: followUpData.title,
          client_id: event.client_id,
          action: 'follow_up_created',
        });
      }

      await createAuditLog('update', 'calendar_event', completingTaskId, {
        status: 'completed',
        action: 'task_completed',
        completion_note: completionNote || ''
      });

      await loadEvents();
      if (editingEvent && editingEvent.id === completingTaskId) {
        setEventFormData({ ...eventFormData, status: 'completed' });
      }

      setSuccess('Task completed successfully!');
      setTimeout(() => setSuccess(''), 2000);
      setShowCompletionModal(false);
      setCompletingTaskId(null);
      setCompletingTaskTypeConfig(null);
      setCompletionNote('');
      setCreateFollowUp(false);
      setFollowUpDate('');
      setFollowUpAssignee('');
      setRescheduleNeeded(false);
      setMeasurementData({ description: '', quantity_dimensions: '', specifications: '', comments: '' });
    } catch (error) {
      setError('Failed to complete task: ' + error.message);
    }
  };

  const navigateMonth = (direction) => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + direction, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const getTodayTasks = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    return events.filter(event => {
      const eventDate = new Date(event.start_date);
      eventDate.setHours(0, 0, 0, 0);
      
      // Filter by today's date and assigned to current user
      const isToday = eventDate.getTime() === today.getTime();
      const isAssignedToUser = event.assigned_to === user?.id || !event.assigned_to;
      
      return isToday && isAssignedToUser;
    }).sort((a, b) => {
      // Sort by start time, all-day events first
      if (a.all_day && !b.all_day) return -1;
      if (!a.all_day && b.all_day) return 1;
      if (a.all_day && b.all_day) return 0;
      
      const timeA = new Date(a.start_date).getTime();
      const timeB = new Date(b.start_date).getTime();
      return timeA - timeB;
    });
  };

  const days = getDaysInMonth(currentDate);
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  if (loading) {
    return (
      <div className="calendar-page">
        <div className="loading-state">Loading calendar...</div>
      </div>
    );
  }

  const todayTasks = getTodayTasks();

  return (
    <div className={`calendar-page ${taskListOpen ? 'task-list-open' : ''}`}>
      <div className="calendar-header">
        <h1>Calendar</h1>
        <div className="calendar-controls">
          <button className="btn-nav" onClick={() => navigateMonth(-1)}>← Previous</button>
          <button className="btn-today" onClick={goToToday}>Today</button>
          <button className="btn-nav" onClick={() => navigateMonth(1)}>Next →</button>
          <button 
            className="btn-task-list" 
            onClick={() => setTaskListOpen(!taskListOpen)}
            title={taskListOpen ? 'Hide Task List' : 'Show Today\'s Tasks'}
          >
            {taskListOpen ? '→' : '←'} Today's Tasks ({todayTasks.length})
          </button>
        </div>
        <h2 className="month-year">
          {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
        </h2>
      </div>

      <div className="calendar-grid">
        <div className="calendar-weekdays">
          {dayNames.map(day => (
            <div key={day} className="weekday-header">{day}</div>
          ))}
        </div>
        <div className="calendar-days">
          {days.map((date, index) => {
            const dayEvents = getEventsForDate(date);
            const isToday = date && date.toDateString() === new Date().toDateString();
            const isCurrentMonth = date && date.getMonth() === currentDate.getMonth();

            return (
              <div
                key={index}
                className={`calendar-day ${!date ? 'empty' : ''} ${isToday ? 'today' : ''} ${!isCurrentMonth ? 'other-month' : ''}`}
                onClick={() => handleDateClick(date)}
              >
                {date && (
                  <>
                    <div className="day-number">{date.getDate()}</div>
                    <div className="day-events">
                      {dayEvents.slice(0, 3).map(event => {
                        // Determine event type for color mapping - use task_type if available
                        let eventType = 'neutral';
                        if (event.status === 'completed') {
                          eventType = 'completed';
                        } else if (event.task_type && taskTypeColors[event.task_type]) {
                          eventType = event.task_type; // Use task_type color
                        } else if (event.clients) {
                          eventType = 'estimate'; // Legacy: Client tasks use estimate orange
                        } else if (event.status === 'scheduled') {
                          eventType = 'invoice'; // Legacy: Scheduled tasks use invoice blue
                        }
                        
                        const colors = taskTypeColors[eventType] || taskTypeColors.neutral;
                        
                        return (
                          <div
                            key={event.id}
                            className="calendar-event event-dot"
                            style={{
                              backgroundColor: colors.background,
                              borderLeft: `4px solid ${colors.border}`,
                              color: colors.text
                            }}
                            title={`${event.title}${event.clients ? ` - Client: ${event.clients.name}` : ''}${event.status ? ` - Status: ${event.status}` : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEventClick(event);
                            }}
                          >
                            {event.title}
                          </div>
                        );
                      })}
                      {dayEvents.length > 3 && (
                        <div className="more-events">+{dayEvents.length - 3} more</div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Event Modal */}
      {showEventModal && (
        <div className="modal-overlay" onClick={() => setShowEventModal(false)}>
          <div className="modal-content calendar-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingEvent ? 'Edit Event' : 'New Event'}</h2>
              <button className="btn-close" onClick={() => setShowEventModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit} className="event-form">
              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">{success}</div>}

              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  required
                  value={eventFormData.title}
                  onChange={(e) => setEventFormData({ ...eventFormData, title: e.target.value })}
                  placeholder="Event title"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  rows="4"
                  value={eventFormData.description}
                  onChange={(e) => setEventFormData({ ...eventFormData, description: e.target.value })}
                  placeholder="Event description"
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={eventFormData.all_day}
                    onChange={(e) => setEventFormData({ ...eventFormData, all_day: e.target.checked })}
                  />
                  <span>All day event</span>
                </label>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Date *</label>
                  <input
                    type="date"
                    required
                    value={eventFormData.start_date}
                    onChange={(e) => setEventFormData({ ...eventFormData, start_date: e.target.value })}
                  />
                </div>
                {!eventFormData.all_day && (
                  <div className="form-group">
                    <label>Start Time *</label>
                    <input
                      type="time"
                      required
                      value={eventFormData.start_time}
                      onChange={(e) => setEventFormData({ ...eventFormData, start_time: e.target.value })}
                    />
                  </div>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={eventFormData.end_date}
                    onChange={(e) => setEventFormData({ ...eventFormData, end_date: e.target.value })}
                  />
                </div>
                {!eventFormData.all_day && (
                  <div className="form-group">
                    <label>End Time</label>
                    <input
                      type="time"
                      value={eventFormData.end_time}
                      onChange={(e) => setEventFormData({ ...eventFormData, end_time: e.target.value })}
                    />
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Link to Client (Optional)</label>
                <select
                  value={eventFormData.client_id}
                  onChange={(e) => setEventFormData({ ...eventFormData, client_id: e.target.value })}
                >
                  <option value="">No client</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Task Type *</label>
                <select
                  value={eventFormData.task_type}
                  onChange={(e) => setEventFormData({ ...eventFormData, task_type: e.target.value })}
                  required
                >
                  {taskTypes.length > 0 ? (
                    taskTypes.filter(tt => tt.active).map(tt => (
                      <option key={tt.id} value={tt.code}>{tt.name}</option>
                    ))
                  ) : (
                    <>
                      <option value="general">General</option>
                      <option value="call">Call</option>
                      <option value="follow_up">Follow-Up</option>
                      <option value="estimate_follow_up">Estimate Follow-Up</option>
                      <option value="invoice_follow_up">Invoice Follow-Up</option>
                      <option value="site_visit">Site Visit</option>
                    </>
                  )}
                </select>
                <small className="form-hint">Select the type of task. This affects completion behavior and calendar colors.</small>
              </div>

              <div className="form-group">
                <h4 className="form-subheading">Arrival &amp; start time (this task)</h4>
                <p className="form-hint" style={{ marginBottom: '0.5rem' }}>
                  When unchecked, the task type default from Settings is used.
                </p>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={eventFormData.require_arrival_time === true}
                    onChange={(e) => setEventFormData(prev => ({ ...prev, require_arrival_time: e.target.checked ? true : null }))}
                  />
                  Require arrival time (user must enter when they arrived)
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={eventFormData.require_start_time === true}
                    onChange={(e) => setEventFormData(prev => ({ ...prev, require_start_time: e.target.checked ? true : null }))}
                  />
                  Require start time (user must enter when they started)
                </label>
              </div>

              <div className="form-group">
                <label>Assign To *</label>
                <select
                  value={eventFormData.assigned_to}
                  onChange={(e) => setEventFormData({ ...eventFormData, assigned_to: e.target.value })}
                  required
                >
                  <option value="">Select a user</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.email} {user.user_metadata?.full_name ? `(${user.user_metadata.full_name})` : ''}
                    </option>
                  ))}
                </select>
                <small className="form-hint">Select a user to assign this task to. Assignee is required.</small>
              </div>

              {editingEvent && editingEvent.status !== 'completed' && (
                <div className="form-group">
                  <label>Task Status</label>
                  <div className="status-buttons">
                    <button
                      type="button"
                      className={`btn-status ${eventFormData.status === 'scheduled' ? 'active' : ''}`}
                      onClick={() => setEventFormData({ ...eventFormData, status: 'scheduled' })}
                    >
                      Scheduled
                    </button>
                    <button
                      type="button"
                      className={`btn-status ${eventFormData.status === 'arrived' ? 'active' : ''}`}
                      onClick={() => setEventFormData({ ...eventFormData, status: 'arrived' })}
                    >
                      Arrived
                    </button>
                    <button
                      type="button"
                      className={`btn-status ${eventFormData.status === 'started' ? 'active' : ''}`}
                      onClick={() => setEventFormData({ ...eventFormData, status: 'started' })}
                    >
                      Started
                    </button>
                    <button
                      type="button"
                      className={`btn-status ${eventFormData.status === 'completed' ? 'active' : ''}`}
                      onClick={() => setEventFormData({ ...eventFormData, status: 'completed' })}
                    >
                      Completed
                    </button>
                  </div>
                </div>
              )}
              
              {editingEvent && editingEvent.status === 'completed' && (
                <div className="form-group">
                  <label>Task Status</label>
                  <div className="status-display">
                    <span className="status-badge-completed">Completed</span>
                    <small className="status-note">This task is completed and cannot be modified.</small>
                  </div>
                </div>
              )}

              <div className="form-actions">
                {editingEvent && editingEvent.status !== 'completed' && (
                  <>
                    <button
                      type="button"
                      className="btn-delete"
                      onClick={() => handleDelete(editingEvent.id)}
                    >
                      Delete
                    </button>
                    {eventFormData.status !== 'arrived' && eventFormData.status !== 'started' && (
                      <button
                        type="button"
                        className="btn-status-action"
                        onClick={() => handleStatusChange(editingEvent.id, 'arrived')}
                      >
                        Mark as Arrived
                      </button>
                    )}
                    {eventFormData.status === 'arrived' && eventFormData.status !== 'started' && (
                      <button
                        type="button"
                        className="btn-status-action"
                        onClick={() => handleStatusChange(editingEvent.id, 'started')}
                      >
                        Mark as Started
                      </button>
                    )}
                    {eventFormData.status !== 'completed' && (
                      <button
                        type="button"
                        className="btn-status-action btn-complete"
                        onClick={() => handleStatusChange(editingEvent.id, 'completed')}
                      >
                        Mark as Completed
                      </button>
                    )}
                  </>
                )}
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowEventModal(false)}
                >
                  {editingEvent && editingEvent.status === 'completed' ? 'Close' : 'Cancel'}
                </button>
                {(!editingEvent || editingEvent.status !== 'completed') && (
                  <button 
                    type="submit" 
                    className="btn-primary"
                    disabled={!eventFormData.title || !eventFormData.assigned_to || !eventFormData.start_date}
                  >
                    {editingEvent ? 'Update Event' : 'Create Event'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Task List Panel */}
      <div className={`task-list-panel ${taskListOpen ? 'open' : ''}`}>
        <div className="task-list-header">
          <h2>Today's Tasks</h2>
          <button className="btn-close-task-list" onClick={() => setTaskListOpen(false)}>×</button>
        </div>
        <div className="task-list-content">
          {todayTasks.length === 0 ? (
            <div className="no-tasks">
              <p>No tasks scheduled for today</p>
            </div>
          ) : (
            <div className="task-items">
              {todayTasks.map(task => (
                <div 
                  key={task.id} 
                  className={`task-item ${task.status ? `status-${task.status}` : ''} ${task.status === 'completed' ? 'task-completed' : ''}`}
                  onClick={() => {
                    if (task.status !== 'completed') {
                      handleEventClick(task);
                    }
                  }}
                  style={{ cursor: task.status === 'completed' ? 'default' : 'pointer' }}
                >
                  <div className="task-item-header">
                    <h3 className="task-title">{task.title}</h3>
                    <span className={`task-status-badge status-${task.status || 'scheduled'}`}>
                      {task.status || 'scheduled'}
                    </span>
                  </div>
                  {task.description && (
                    <p className="task-description">{task.description}</p>
                  )}
                  <div className="task-meta">
                    {task.all_day ? (
                      <span className="task-time">All Day</span>
                    ) : (
                      <span className="task-time">
                        {new Date(task.start_date).toLocaleTimeString('en-US', { 
                          hour: 'numeric', 
                          minute: '2-digit',
                          hour12: true 
                        })}
                        {task.end_date && ` - ${new Date(task.end_date).toLocaleTimeString('en-US', { 
                          hour: 'numeric', 
                          minute: '2-digit',
                          hour12: true 
                        })}`}
                      </span>
                    )}
                    {task.clients && (
                      <span className="task-client">Client: {task.clients.name}</span>
                    )}
                  </div>
                  {task.status !== 'completed' && (() => {
                    const typeConfig = taskTypes.find(tt => tt.code === (task.task_type || 'general'));
                    const typeActions = typeConfig?.completion_actions || {};
                    const showMarkArrived = typeActions.require_arrival_time === true;
                    const showMarkStarted = typeActions.require_start_time === true;
                    const showComplete = typeActions.hide_complete_button !== true;
                    const hasAnyAction = showMarkArrived || showMarkStarted || showComplete;
                    if (!hasAnyAction) return null;
                    return (
                      <div className="task-actions">
                        {showMarkArrived && task.status !== 'arrived' && task.status !== 'started' && (
                          <button
                            className="btn-task-action btn-arrived"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStatusChange(task.id, 'arrived');
                            }}
                          >
                            Mark Arrived
                          </button>
                        )}
                        {showMarkStarted && task.status === 'arrived' && task.status !== 'started' && (
                          <button
                            className="btn-task-action btn-started"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStatusChange(task.id, 'started');
                            }}
                          >
                            Mark Started
                          </button>
                        )}
                        {showComplete && (
                          <button
                            className="btn-task-action btn-completed"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStatusChange(task.id, 'completed');
                            }}
                          >
                            Complete
                          </button>
                        )}
                      </div>
                    );
                  })()}
                  {task.status === 'completed' && (
                    <div className="task-completed-note">
                      <small>This task is completed and cannot be modified.</small>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Completion Modal */}
      {showCompletionModal && (
        <div className="modal-overlay" onClick={() => setShowCompletionModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Complete Task</h2>
              <button className="btn-close-modal" onClick={() => {
                setShowCompletionModal(false);
                setCompletingTaskId(null);
                setCompletingTaskTypeConfig(null);
                setCompletionNote('');
                setCreateFollowUp(false);
                setFollowUpDate('');
                setFollowUpAssignee('');
                setRescheduleNeeded(false);
                setMeasurementData({ description: '', quantity_dimensions: '', specifications: '', comments: '' });
              }}>×</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {error && <div className="error-message">{error}</div>}
              
              <div className="completion-note-form">
                {completingTaskTypeConfig?.completion_actions?.allow_reschedule_needed && (
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={rescheduleNeeded}
                        onChange={(e) => setRescheduleNeeded(e.target.checked)}
                      />
                      Reschedule needed (complete with a note only)
                    </label>
                  </div>
                )}

                {completingTaskTypeConfig?.completion_actions?.requires_measurements && !rescheduleNeeded && (
                  <div className="completion-measurements-section" style={{ marginBottom: '1rem', padding: '1rem', background: '#f7fafc', borderRadius: '6px' }}>
                    <h3 style={{ marginTop: 0, marginBottom: '0.75rem', fontSize: '1rem' }}>Measurements (links to order form)</h3>
                    <div className="form-group">
                      <label>Description</label>
                      <input
                        type="text"
                        value={measurementData.description}
                        onChange={(e) => setMeasurementData(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="e.g., Replace IG Unit"
                      />
                    </div>
                    <div className="form-group">
                      <label>Quantity / Dimensions / Thickness</label>
                      <input
                        type="text"
                        value={measurementData.quantity_dimensions}
                        onChange={(e) => setMeasurementData(prev => ({ ...prev, quantity_dimensions: e.target.value }))}
                        placeholder="e.g., 1- 23 7/8&quot; X 26&quot; ... 1/2&quot; OA"
                      />
                    </div>
                    <div className="form-group">
                      <label>Specifications</label>
                      <input
                        type="text"
                        value={measurementData.specifications}
                        onChange={(e) => setMeasurementData(prev => ({ ...prev, specifications: e.target.value }))}
                        placeholder="e.g., Low-E temp / mill / CLR Temp"
                      />
                    </div>
                    <div className="form-group">
                      <label>Comments</label>
                      <textarea
                        value={measurementData.comments}
                        onChange={(e) => setMeasurementData(prev => ({ ...prev, comments: e.target.value }))}
                        placeholder="Additional notes..."
                        rows="2"
                      />
                    </div>
                  </div>
                )}

                {(completingTaskTypeConfig?.completion_actions?.allow_completion_note !== false) && (
                  <div className="form-group">
                    <label>
                      {completingTaskTypeConfig?.completion_actions?.completion_note_label || 'Completion Notes'}
                      {completingTaskTypeConfig?.completion_actions?.require_completion_note && ' *'}
                    </label>
                    <textarea
                      value={completionNote}
                      onChange={(e) => setCompletionNote(e.target.value)}
                      placeholder={completingTaskTypeConfig?.completion_actions?.completion_note_placeholder || 'Add any notes about task completion...'}
                      rows="4"
                      className="completion-note-input"
                    />
                  </div>
                )}

                {completingTaskTypeConfig?.completion_actions?.allow_create_follow_up && (
                  <div className="form-group">
                    <div className="checkbox-group">
                      <input
                        type="checkbox"
                        id="createFollowUp"
                        checked={createFollowUp}
                        onChange={(e) => {
                          setCreateFollowUp(e.target.checked);
                          if (!e.target.checked) {
                            setFollowUpDate('');
                            setFollowUpAssignee('');
                          }
                        }}
                      />
                      <label htmlFor="createFollowUp" className="checkbox-label">
                        {completingTaskTypeConfig?.completion_actions?.follow_up_label || 'Create Follow-Up Task'}
                      </label>
                    </div>
                    <small className="form-hint">
                      {completingTaskTypeConfig?.completion_actions?.follow_up_hint || 'Check this to automatically create a follow-up task after completing this one.'}
                    </small>
                  </div>
                )}

                {createFollowUp && completingTaskTypeConfig?.completion_actions?.allow_create_follow_up && (
                  <div className="follow-up-section" style={{ marginTop: '1rem', padding: '1rem', background: '#f7fafc', borderRadius: '6px' }}>
                    <h3 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1rem' }}>Follow-Up Task Details</h3>
                    
                    <div className="form-group">
                      <label>Follow-Up Date *</label>
                      <input
                        type="date"
                        required={createFollowUp}
                        value={followUpDate}
                        onChange={(e) => setFollowUpDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>

                    <div className="form-group">
                      <label>Assign To *</label>
                      <select
                        required={createFollowUp}
                        value={followUpAssignee}
                        onChange={(e) => setFollowUpAssignee(e.target.value)}
                      >
                        <option value="">Select a user</option>
                        {users.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.email} {user.user_metadata?.full_name ? `(${user.user_metadata.full_name})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', paddingTop: '1.5rem', borderTop: '1px solid #e2e8f0' }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setShowCompletionModal(false);
                  setCompletingTaskId(null);
                  setCompletingTaskTypeConfig(null);
                  setCompletionNote('');
                  setCreateFollowUp(false);
                  setFollowUpDate('');
                  setFollowUpAssignee('');
                  setRescheduleNeeded(false);
                  setMeasurementData({ description: '', quantity_dimensions: '', specifications: '', comments: '' });
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleCompleteTask}
              >
                Complete Task
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Arrival / Start time entry modal */}
      {showArrivalStartModal && pendingStatusChange && (
        <div className="modal-overlay" onClick={() => { setShowArrivalStartModal(false); setPendingStatusChange(null); setStatusChangeTime(''); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {pendingStatusChange.newStatus === 'arrived' ? 'Record arrival time' : 'Record start time'}
              </h2>
              <button
                className="btn-close-modal"
                onClick={() => { setShowArrivalStartModal(false); setPendingStatusChange(null); setStatusChangeTime(''); }}
              >
                ×
              </button>
            </div>
            <div className="modal-body" style={{ padding: '1.5rem' }}>
              {error && <div className="error-message">{error}</div>}
              <div className="form-group">
                <label>
                  {pendingStatusChange.newStatus === 'arrived' ? 'Arrival time' : 'Start time'}
                </label>
                <input
                  type="datetime-local"
                  value={statusChangeTime}
                  onChange={(e) => setStatusChangeTime(e.target.value)}
                  className="completion-note-input"
                />
              </div>
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', paddingTop: '1.5rem', borderTop: '1px solid #e2e8f0' }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => { setShowArrivalStartModal(false); setPendingStatusChange(null); setStatusChangeTime(''); }}
              >
                Cancel
              </button>
              <button type="button" className="btn-primary" onClick={handleArrivalStartTimeConfirm}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
